import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { usePsychologistContext } from '@/contexts/PsychologistContext';
import { useContactChannel } from '@/hooks/useContactChannel';
import { hasSeenContactChannelNotice, markContactChannelNoticeSeen } from '@/utils/contactChannelNotice';

/**
 * Aviso único (una sola vez, "Entendido") de que el canal de contacto cambió — solo para el
 * segmento nuevo (`psychologist_psichat_inbox`). NO se muestra a NZ/O-desde-NZ (fee_type=range):
 * ellas ya tenían este canal de antes, no es una novedad. La exclusión la hace el backend
 * (`show_contact_channel_notice`, ver useContactChannel) — este componente no reevalúa esa regla.
 *
 * Sin persistencia server-side (decisión explícita): usa localStorage por psychologistId, mismo
 * patrón que timezoneAgendaNotice.ts. Si entra desde otro dispositivo puede volver a verlo una vez.
 */
export function ContactChannelUpdatedModal() {
  const { psychologist } = usePsychologistContext();
  const { email, whatsappNumber, whatsappDisplay, showContactChannelNotice, loading } = useContactChannel();
  const [dismissed, setDismissed] = useState(false);

  const psychologistId = psychologist?.id || '';
  const shouldShow =
    !loading &&
    showContactChannelNotice &&
    !!psychologistId &&
    !dismissed &&
    !hasSeenContactChannelNotice(psychologistId);

  if (!shouldShow) return null;

  const handleAccept = () => {
    markContactChannelNoticeSeen(psychologistId);
    setDismissed(true);
  };

  return (
    <Dialog open onOpenChange={(open) => { if (!open) handleAccept(); }}>
      <DialogContent
        style={{ maxWidth: '600px', padding: '2rem' }}
        onInteractOutside={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>¡Hola colega!</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 text-sm text-gray-600 leading-relaxed">
          <p>
            Queremos contarte que a partir de hoy, estamos actualizando los métodos de contacto
            que usás para charlar con nosotros. No te preocupes, esto solo afecta a las líneas
            de contacto entre vos, terapeuta, y el equipo interno de Psi Mammoliti.
          </p>
          <p>
            La idea detrás de este cambio es facilitarte la comunicación con nosotros, y que no
            tengas que saber de memoria adónde contactarte según la temática que necesites
            charlar 🙂
          </p>
          <p className="font-medium text-gray-700">De ahora en más, nos encontrás en los siguientes canales:</p>
          <ul className="space-y-2 pl-1">
            <li>
              <span className="font-semibold text-gray-800">WhatsApp:</span> cualquier tema que sea
              urgente y/o requiera gestión en el mismo día, te invitamos a escribirnos al{' '}
              <a
                href={`https://wa.me/${whatsappNumber}`}
                target="_blank"
                rel="noopener noreferrer"
                className="font-medium text-violet-700 hover:underline"
              >
                {whatsappDisplay}
              </a>.
            </li>
            <li>
              <span className="font-semibold text-gray-800">Email:</span> cualquier tema que
              involucre gestiones no urgentes, desde dudas sobre políticas, asignaciones de
              pacientes, performance — lo que no requiera respuesta inmediata → escribinos a{' '}
              <a href={`mailto:${email}`} className="font-medium text-violet-700 hover:underline">
                {email}
              </a>.
            </li>
          </ul>
          <p className="text-gray-500">Gracias! — Equipo Psi Mammoliti</p>
          <Button onClick={handleAccept} className="w-full">Entendido</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
